import {useEffect,useRef,useState} from 'react';
import {auth,db} from './firebase';
import {readInsightHistory,writeInsightHistory} from './private-cache';
import {appendSavedInsights,loadSavedInsights,mergePendingInsights,removeSavedInsight} from './insights-store';

export default function useInsightHistory(uid){
  const [history,setHistory]=useState(()=>readInsightHistory(uid)),[busy,setBusy]=useState(false),[notice,setNotice]=useState(''),[problem,setProblem]=useState(false),[canRetry,setCanRetry]=useState(false);
  const latest=useRef(history),active=useRef(true),lock=useRef(false),revision=useRef(0);
  const current=()=>active.current&&auth.currentUser?.uid===uid;
  const publish=rows=>{latest.current=rows;setHistory(rows);return writeInsightHistory(uid,rows);};
  async function refresh(){
    const version=revision.current;
    try{
      const rows=await loadSavedInsights(db,uid);
      if(!current()||version!==revision.current)return;
      const merged=mergePendingInsights(rows,latest.current);publish(merged);
      const pending=merged.some(entry=>entry.pendingSync);
      setProblem(false);setCanRetry(pending);setNotice(pending?'An analysis is saved on this device and waiting to sync.':'');
    }catch{
      if(current()&&version===revision.current){setProblem(true);setCanRetry(true);setNotice('Saved history could not be loaded. Any history shown here is the copy on this device.');}
    }
  }
  useEffect(()=>{active.current=true;revision.current++;refresh();return()=>{revision.current++;active.current=false;};},[uid]); // eslint-disable-line react-hooks/exhaustive-deps
  async function syncPending(){
    if(lock.current||!current())return false;
    const pending=latest.current.filter(entry=>entry.pendingSync);
    if(!pending.length){await refresh();return true;}
    lock.current=true;revision.current++;setBusy(true);setProblem(false);setNotice('Saving analysis history…');setCanRetry(false);
    try{
      const rows=await appendSavedInsights(db,uid,pending);
      if(!current())return false;
      publish(rows);setNotice('Analysis history saved across your devices.');return true;
    }catch{
      if(current()){
        const cached=writeInsightHistory(uid,latest.current);
        setProblem(true);setCanRetry(true);setNotice(cached?'The analysis is saved on this device, but cloud sync failed. Retry without running another analysis.':'Cloud sync and device storage are unavailable. Keep this page open and retry to save your analysis.');
      }
      return false;
    }finally{lock.current=false;if(current())setBusy(false);}
  }
  async function save(entry){
    if(!current())return false;
    revision.current++;
    publish([{...entry,pendingSync:true},...latest.current.filter(row=>String(row.id)!==String(entry.id))].slice(0,20));
    return syncPending();
  }
  async function remove(id){
    if(lock.current||!current())return false;
    lock.current=true;revision.current++;setBusy(true);setNotice('Removing the saved analysis…');setProblem(false);setCanRetry(false);
    try{
      const rows=await removeSavedInsight(db,uid,id);
      if(!current())return false;
      publish(mergePendingInsights(rows,latest.current.filter(row=>String(row.id)!==String(id))));
      setNotice('Saved analysis removed.');setCanRetry(latest.current.some(row=>row.pendingSync));return true;
    }catch{
      if(current()){setProblem(true);setNotice('The analysis could not be removed. It is still in your history; try deleting it again.');}
      return false;
    }finally{lock.current=false;if(current())setBusy(false);}
  }
  return {history,busy,notice,problem,canRetry,save,remove,retry:syncPending};
}
